import { useState } from "react"
import { motion } from "framer-motion"
import { Search, DollarSign, Clock, Heart, Zap, MessageSquare } from "lucide-react"
import Layout from "@/components/Layout"
import AssistantCard from "@/components/AssistantCard"
import SectionHeader from "@/components/SectionHeader"
import { ASSISTANTS_LIST } from "@/lib/index"
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "learn-grow", label: "Learn & Grow" },
  { id: "money-work", label: "Money & Work" },
  { id: "home-support", label: "Home & Support" },
]

const EXAMPLE_PROMPTS = [
  "Help my Grade 7 child with fractions",
  "How do I know if a WhatsApp message is a scam?",
  "Help me build a monthly budget",
  "I want to start a small business",
]

const BENEFITS = [
  { icon: DollarSign, text: "Saves money" },
  { icon: Clock, text: "Saves time" },
  { icon: Heart, text: "Reduces stress" },
  { icon: Zap, text: "Available anytime" },
  { icon: MessageSquare, text: "No jargon" },
]

export default function Assistants() {
  const [activeCategory, setActiveCategory] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")

  const filteredAssistants = ASSISTANTS_LIST.filter((assistant) => {
    const categoryMap: Record<string, string> = {
      "learn-grow": "Learn & Grow",
      "money-work": "Money & Work",
      "home-support": "Home & Support",
    }

    const matchesCategory =
      activeCategory === "all" ||
      assistant.category === categoryMap[activeCategory]

    const matchesSearch =
      searchQuery === "" ||
      assistant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assistant.description.toLowerCase().includes(searchQuery.toLowerCase())

    return matchesCategory && matchesSearch
  })

  return (
    <Layout>
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              22 assistants for everyday life
            </h1>
            <p className="text-xl text-muted-foreground">
              Practical support for the whole family — covering money, education, health, and daily life through WhatsApp
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={springPresets.gentle}
            className="max-w-6xl mx-auto"
          >
            <p className="text-center text-sm font-medium text-muted-foreground mb-6">
              Try asking:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {EXAMPLE_PROMPTS.map((prompt, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="bg-card border rounded-2xl px-6 py-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <p className="text-sm font-medium text-center">
                    "{prompt}"
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 border-b">
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={springPresets.gentle}
            className="max-w-4xl mx-auto space-y-8"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search assistants by name or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-lg"
              />
            </div>

            <div className="flex flex-wrap gap-3 justify-center">
              {CATEGORIES.map((category) => (
                <Button
                  key={category.id}
                  variant={activeCategory === category.id ? "default" : "outline"}
                  onClick={() => setActiveCategory(category.id)}
                  className="rounded-full px-6"
                >
                  {category.label}
                </Button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="mb-8 text-center"
          >
            <p className="text-lg text-muted-foreground">
              Showing {filteredAssistants.length} of {ASSISTANTS_LIST.length} assistants
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {filteredAssistants.map((assistant) => (
              <motion.div key={assistant.id} variants={staggerItem}>
                <AssistantCard assistant={assistant} />
              </motion.div>
            ))}
          </motion.div>

          {filteredAssistants.length === 0 && (
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              className="text-center py-16"
            >
              <p className="text-xl text-muted-foreground">
                No assistants found matching your search.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("")
                  setActiveCategory("all")
                }}
                className="mt-6"
              >
                Clear filters
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-2 md:grid-cols-5 gap-8 max-w-5xl mx-auto"
          >
            {BENEFITS.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="flex flex-col items-center text-center gap-3"
                >
                  <div className="w-12 h-12 rounded-full bg-primary-foreground/10 flex items-center justify-center">
                    <Icon className="h-6 w-6" />
                  </div>
                  <p className="font-medium">{benefit.text}</p>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-3xl mx-auto text-center"
          >
            <SectionHeader
              headline="Start with one question today"
              subheadline="Discover how Sammo can help your family with practical everyday support"
              ctaText="Start Free Trial"
              ctaHref="/family-access"
              align="center"
            />
          </motion.div>
        </div>
      </section>
    </Layout>
  )
}